import React from 'react';
import { cn } from '../../poker-ui/utils/cn';
import { belotMatchCpFromEnginePoints, belotMatchTargetCpFromEngine } from '../../game/belotArmenianCards';

/**
 * @param {unknown} engineDelta
 * @returns {string}
 */
function fmtDeltaCp(engineDelta) {
  const n = Number(engineDelta);
  if (!Number.isFinite(n)) return '—';
  const cp = Math.round(n / 10);
  if (cp === 0) return '0';
  if (cp > 0) return `+${cp}`;
  return String(cp);
}

/**
 * Локальный итог раунда — см. `extractRoundSummary` в `BelotArmenianLocalLobby.jsx`.
 * @typedef {{
 *   delta?: [number, number],
 *   success?: boolean,
 *   bait?: boolean,
 *   teamScoresAfter?: [number, number],
 * } | null} RoundSummaryLike
 */

/**
 * Модалка «лист в линейку» с очками за раунд и счётом матча (мы vs соперники).
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   summary: RoundSummaryLike,
 *   heroTeamIdx: 0 | 1,
 *   targetScore: number,
 * }} props
 */
export function BelotRoundNotebookModal({ open, onClose, summary, heroTeamIdx, targetScore }) {
  if (!open) return null;

  const oppTeamIdx = heroTeamIdx === 0 ? 1 : 0;
  const hasSummary =
    summary &&
    typeof summary === 'object' &&
    Array.isArray(summary.teamScoresAfter) &&
    summary.teamScoresAfter.length >= 2;

  const myDelta = hasSummary ? fmtDeltaCp(summary.delta?.[heroTeamIdx]) : '—';
  const oppDelta = hasSummary ? fmtDeltaCp(summary.delta?.[oppTeamIdx]) : '—';
  const myTotal = hasSummary
    ? belotMatchCpFromEnginePoints(summary.teamScoresAfter[heroTeamIdx])
    : '—';
  const oppTotal = hasSummary
    ? belotMatchCpFromEnginePoints(summary.teamScoresAfter[oppTeamIdx])
    : '—';
  const targetCp = belotMatchTargetCpFromEngine(targetScore);

  const success = Boolean(summary?.success);
  const bait = Boolean(summary?.bait);
  const footnote =
    hasSummary && typeof summary.success === 'boolean'
      ? success
        ? bait
          ? 'Контракт выполнен · бейт'
          : 'Контракт выполнен'
        : bait
          ? 'Контракт не взят · бейт'
          : 'Контракт не взят'
      : null;

  const lineBg =
    'repeating-linear-gradient(to bottom, transparent 0, transparent 26px, rgba(120,113,108,0.22) 26px, rgba(120,113,108,0.22) 27px)';

  return (
    <div
      className="fixed inset-0 z-[59] flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={cn(
          'relative w-full max-w-sm rounded-sm border border-stone-600/80 shadow-[0_20px_50px_rgba(0,0,0,0.45)]',
          'bg-[#f4ecd8] text-stone-900',
        )}
        style={{ backgroundImage: lineBg }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="belot-round-notebook-title"
      >
        <div className="relative px-4 pb-4 pt-5">
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-4 border-b border-rose-300/70"
            aria-hidden
          />
          <h2
            id="belot-round-notebook-title"
            className="mb-6 text-center font-serif text-base font-bold tracking-tight text-stone-800"
          >
            Итог раунда
          </h2>

          {!hasSummary ? (
            <p className="mb-6 text-center font-serif text-sm leading-relaxed text-stone-700">
              Раунд посчитан.
              <br />
              Нажмите «Следующий раунд» внизу экрана.
            </p>
          ) : (
            <>
              <table className="mb-1 w-full border-collapse font-serif text-sm">
                <thead>
                  <tr className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">
                    <th className="pb-1 pr-2 text-left font-normal normal-case text-stone-500">Команда</th>
                    <th className="px-1 pb-1 text-center">За раунд</th>
                    <th className="pb-1 pl-1 text-center">В матче</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="text-stone-900">
                    <td className="py-0.5 pr-2 text-left font-semibold text-stone-800">Моя команда</td>
                    <td className="px-1 py-0.5 text-center font-mono tabular-nums">{myDelta}</td>
                    <td className="py-0.5 pl-1 text-center font-mono tabular-nums font-semibold">{myTotal}</td>
                  </tr>
                  <tr className="text-stone-900">
                    <td className="py-0.5 pr-2 text-left font-semibold text-stone-800">Соперники</td>
                    <td className="px-1 py-0.5 text-center font-mono tabular-nums">{oppDelta}</td>
                    <td className="py-0.5 pl-1 text-center font-mono tabular-nums font-semibold">{oppTotal}</td>
                  </tr>
                </tbody>
              </table>
              <p className="mt-4 text-center font-mono text-[11px] tabular-nums text-stone-600">
                Цель матча: {targetCp} CP
              </p>
              {footnote ? (
                <p
                  className={cn(
                    'mt-2 text-center font-serif text-xs',
                    success ? 'text-emerald-800/95' : 'text-rose-800/95',
                  )}
                >
                  {footnote}
                </p>
              ) : null}
            </>
          )}

          <button
            type="button"
            onClick={onClose}
            className="mt-6 w-full rounded-md border border-stone-500/60 bg-stone-200/80 py-2 font-serif text-sm font-semibold text-stone-800 shadow-inner transition hover:bg-stone-100"
          >
            Понятно
          </button>
        </div>
      </div>
    </div>
  );
}
